import { timeToMinutes, toTwo, addMinutesToTime } from './utils';
import { isLocalRangeWithinTemplate, doesOverlapExistingTemplate } from './validation';
import { getNowInSelectedTZParts } from './timeZoneHelpers';
import { isImpersonated } from '../../../hooks/api/auth/tokenManager';
import { DragState, DragVisual } from './types';

export interface MouseUpParams {
  dragging: DragState | null;
  dragVisual: DragVisual | null;
  dragBlocked: boolean;
  editMode: boolean;
  template: { id: string; weekday: number; startTime: string; endTime: string; active: boolean }[];
  startOfWeek: Date;
  psychologistId: number | string;
  hasOverlapAt: (date: string, start: number, end: number) => boolean;
  now: Date;
  selectedTimeZone: string;
  visibleTimeSlots: string[];
  gridRef: React.RefObject<HTMLDivElement>;
  clientX: number;
  clientY: number;
  dayDate: string;
  dayIdx: number;
  rowHeightPx: number;
  quarterHeightPx: number;
}

export function handleCellMouseUp(params: MouseUpParams): {
  shouldReset: boolean;
  templateModal?: { date: string; startTime: string; endTime: string; weekdayIdx: number };
  dragModal?: { date: string; startTime: string; endTime: string };
} {
  const { dragging, dragVisual, dragBlocked, editMode, template, startOfWeek, hasOverlapAt, now, selectedTimeZone, visibleTimeSlots, gridRef, clientX, clientY, dayDate, dayIdx, rowHeightPx, quarterHeightPx } = params;

  if (!dragging || !dragVisual || !gridRef.current) return { shouldReset: false };

  if (dragBlocked) return { shouldReset: true };

  const rect = gridRef.current.getBoundingClientRect();
  if (clientX < rect.left || clientX > rect.right || clientY < rect.top || clientY > rect.bottom) {
    return { shouldReset: true };
  }

  let y = clientY - rect.top;
  const firstRowEl = gridRef.current.querySelector('[data-first-time-row="true"]') as HTMLDivElement | null;
  const headerHeight = firstRowEl ? (firstRowEl.getBoundingClientRect().top - rect.top) : 0;
  y = Math.max(0, Math.min(y, rect.height));
  let yAdj = y - headerHeight;
  yAdj = Math.max(0, Math.min(yAdj, rect.height - headerHeight));
  const rowIdxFromAdj2 = Math.floor(yAdj / rowHeightPx);
  const posInRowUp = yAdj - rowIdxFromAdj2 * rowHeightPx;
  const quarterIndexUp = Math.floor(posInRowUp / quarterHeightPx);
  y = headerHeight + rowIdxFromAdj2 * rowHeightPx;
  const rowIdx2 = Math.floor((y - headerHeight) / rowHeightPx);
  const base = visibleTimeSlots[Math.max(0, Math.min(visibleTimeSlots.length - 1, rowIdx2))] || dragging.startTime;
  const tzNow = getNowInSelectedTZParts(selectedTimeZone);
  const todayStr = tzNow.todayStr;
  const [dyU, dmU, ddU] = dayDate.split('-').map(Number);
  const dayObj = new Date(dyU, dmU - 1, ddU);
  dayObj.setHours(0, 0, 0, 0);
  const today0Up = new Date(now);
  today0Up.setHours(0, 0, 0, 0);
  const isPastDayUp = dayObj < today0Up;
  const baseHourStr2 = base.split(':')[0];
  const quarterStr2 = ['00', '30'][quarterIndexUp] || '00';
  const adjustedStart2 = `${baseHourStr2}:${quarterStr2}`;
  const tryStartUp = timeToMinutes(adjustedStart2);
  const tryEndUp = tryStartUp + 60;

  if (editMode) {
    // El rango se selecciona dentro de un solo día: si se soltó en una columna
    // distinta a la de origen, se cancela sin crear nada (defensa por si un salto
    // rápido entre frames evitó la cancelación en el mousemove).
    if (dragging.date && dayDate !== dragging.date) {
      return { shouldReset: true };
    }

    const startTimeLocal = dragging.startTime || adjustedStart2;
    const endTimeLocal = dragging.endTime || addMinutesToTime(startTimeLocal, 60);
    const start0Local = new Date(startOfWeek);
    start0Local.setHours(0, 0, 0, 0);
    const [dyy2, dmm2, ddd2] = dayDate.split('-').map(Number);
    const dragDay2 = new Date(dyy2 || 1970, (dmm2 || 1) - 1, ddd2 || 1);
    dragDay2.setHours(0, 0, 0, 0);
    const weekdayLocalIdxDrag2 = Math.max(0, Math.min(6, Math.round((dragDay2.getTime() - start0Local.getTime()) / 86400000)));
    const startMin2 = timeToMinutes(startTimeLocal);
    const endMin2 = timeToMinutes(endTimeLocal);

    if (doesOverlapExistingTemplate(template, weekdayLocalIdxDrag2, startMin2, endMin2)) {
      return { shouldReset: true };
    }

    return {
      shouldReset: true,
      templateModal: { date: dayDate, startTime: startTimeLocal, endTime: endTimeLocal, weekdayIdx: dayIdx }
    };
  }

  const isInTemplateUp = isLocalRangeWithinTemplate(template, startOfWeek, dayDate, adjustedStart2, 60);
  const impersonating = isImpersonated();
  if (!isInTemplateUp || (isPastDayUp && !impersonating)) return { shouldReset: true };

  const tzNowMinutesUp = timeToMinutes(`${tzNow.hour}:${toTwo(tzNow.minute)}`);
  if (dayDate === todayStr && tryStartUp < tzNowMinutesUp && !impersonating) {
    return { shouldReset: true };
  }

  const hasOverlap = hasOverlapAt(dayDate, tryStartUp, tryEndUp);
  if (hasOverlap) return { shouldReset: true };

  let startTimePrecise = adjustedStart2;
  if (dragVisual && gridRef.current) {
    const gridRect2 = gridRef.current.getBoundingClientRect();
    const firstRowEl2 = gridRef.current.querySelector('[data-first-time-row="true"]') as HTMLDivElement | null;
    const headerH2 = firstRowEl2 ? (firstRowEl2.getBoundingClientRect().top - gridRect2.top) : 0;
    // dragVisual.startY está en coordenadas de contenido (incluye scrollTop);
    // lo descontamos para obtener el offset relativo a la primera fila visible.
    const scrollTop2 = gridRef.current.scrollTop || 0;
    const startYRel = Math.max(0, Math.min(dragVisual.startY - headerH2 - scrollTop2, gridRect2.height - headerH2));
    const startRowIdx = Math.floor(startYRel / rowHeightPx);
    const posInStartRow = startYRel - startRowIdx * rowHeightPx;
    const qIdx = Math.floor(Math.max(0, Math.min(posInStartRow, rowHeightPx - 1)) / quarterHeightPx);
    const baseLabelStart = visibleTimeSlots[Math.max(0, Math.min(visibleTimeSlots.length - 1, startRowIdx))] || adjustedStart2;
    const baseHourStart = baseLabelStart.split(':')[0] || adjustedStart2.split(':')[0];
    const minStr = ['00', '30'][qIdx] || '00';
    startTimePrecise = `${baseHourStart}:${minStr}`;
  } else if (dragging && dragging.startTime) {
    startTimePrecise = dragging.startTime;
  }

  const endTimePrecise = addMinutesToTime(startTimePrecise, 60);
  const rangeOk = isLocalRangeWithinTemplate(template, startOfWeek, dragging?.date || dayDate, startTimePrecise, 60);
  if (!rangeOk) return { shouldReset: true };

  const datePrecise = dragging?.date || dayDate;
  return {
    shouldReset: true,
    dragModal: { date: datePrecise, startTime: startTimePrecise, endTime: endTimePrecise }
  };
}

export function calculateSnappedPosition(params: {
  clientY: number;
  gridRect: DOMRect;
  headerHeight: number;
  rowHeightPx: number;
  quarterHeightPx: number;
}): { snappedTop: number; rowIdx: number; quarterIndex: number } {
  let y = params.clientY - params.gridRect.top;
  y = Math.max(0, Math.min(y, params.gridRect.height));
  let yAdj = y - params.headerHeight;
  yAdj = Math.max(0, Math.min(yAdj, params.gridRect.height - params.headerHeight));
  const rowIdx = Math.floor(yAdj / params.rowHeightPx);
  const posInRow = yAdj - rowIdx * params.rowHeightPx;
  const quarterIndex = Math.floor(posInRow / params.quarterHeightPx);
  const snappedTop = params.headerHeight + rowIdx * params.rowHeightPx + quarterIndex * params.quarterHeightPx;
  return { snappedTop, rowIdx, quarterIndex };
}

